import React from 'react';
import {
  MessageSquare,
  Clock,
  Calendar,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { ExpandableHtmlContent } from '../../ui/ExpandableHtmlContent';
import { LinkClickHandler } from '../../ui/LinkClickHandler';
import { MeetingRecordingCard } from '../../meetings/MeetingRecordingCard';
import type { LeadNote, SalesActivity } from '../../../lib/types';
import type { MeetingRecording } from '../../../lib/types/meetingRecording';

interface DealClientFeedTimelineProps {
  notes: LeadNote[];
  activities: SalesActivity[];
  recordings: MeetingRecording[];
  language: string;
  formatDate: (dateStr: string) => string;
  /** Renderer per l'icona della activity (es. Phone/Video/etc) usato come fallback
   *  quando l'assignee non ha avatar/iniziali */
  getActivityIcon: (activity: SalesActivity) => React.ReactNode;
  getActivityTypeLabel: (type?: string) => string;
  getActivityStatusClass: (status?: string) => string;
}

// Helper inline: converte URL plain text in <a> per `outcome` libero.
const convertUrlsToLinks = (text: string): string => {
  if (!text) return '';
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  return text.replace(
    urlRegex,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>'
  );
};

/**
 * Timeline unificata cronologica per il pannello cliente del DealForm:
 * unisce note + activities + meeting recordings in un'unica lista
 * ordinata desc per `created_at`, identica come pattern a quella del
 * Lead detail.
 *
 * Estratta da DealForm per separare il blocco di render (~200 righe)
 * dalla logica del form.
 */
export function DealClientFeedTimeline({
  notes,
  activities,
  recordings,
  language,
  formatDate,
  getActivityIcon,
  getActivityTypeLabel,
  getActivityStatusClass,
}: DealClientFeedTimelineProps) {
  const hasAny = notes.length > 0 || activities.length > 0 || recordings.length > 0;
  if (!hasAny) return null;

  type TimelineItem =
    | { type: 'note'; date: string; data: LeadNote }
    | { type: 'activity'; date: string; data: SalesActivity }
    | { type: 'recording'; date: string; data: MeetingRecording };

  const items: TimelineItem[] = [
    ...notes.map(n => ({ type: 'note' as const, date: n.created_at, data: n })),
    ...activities.map(a => ({ type: 'activity' as const, date: a.created_at || new Date(0).toISOString(), data: a })),
    ...recordings.map(r => ({ type: 'recording' as const, date: r.created_at, data: r })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <>
      <div className="flex items-center gap-1.5 mt-1 mb-0.5">
        <MessageSquare className="h-3 w-3 text-black/55 dark:text-white/55" />
        <h4 className="text-[11px] font-medium uppercase tracking-wide text-black/55 dark:text-white/55">
          {language === 'it' ? 'Note e attività' : 'Notes & activities'}
          <span className="ml-1 text-black/35 dark:text-white/35 normal-case">
            ({notes.length + activities.length + recordings.length})
          </span>
        </h4>
      </div>

      {items.map(item => {
        if (item.type === 'recording') {
          return <MeetingRecordingCard key={`rec-${item.data.id}`} recording={item.data} />;
        }
        if (item.type === 'note') {
          const note = item.data;
          return (
            <div
              key={`note-${note.id}`}
              className="group p-2.5 bg-white/70 dark:bg-white/[0.03] hover:bg-white dark:hover:bg-white/[0.05] rounded-md border border-black/[0.06] dark:border-white/[0.06] transition-colors"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className="flex-shrink-0">
                  {/* avatar_url (admin) prevale su profile_image_url (user) —
                      stesso pattern dell'header in Layout.tsx */}
                  {((note.author as any)?.avatar_url || note.author?.profile_image_url) ? (
                    <div className="h-4 w-4 rounded-full overflow-hidden border border-black/10">
                      <img
                        src={(note.author as any)?.avatar_url || note.author?.profile_image_url}
                        alt={note.author?.full_name || note.author?.email || 'User'}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-4 w-4 rounded-full bg-[#F1F1EF] dark:bg-[#454B4E] flex items-center justify-center">
                      <span className="text-[9px] font-medium text-[#37352F] dark:text-white">
                        {(note.author?.full_name || note.author?.email || 'U')
                          .split(' ')
                          .map((part: string) => part[0])
                          .join('')
                          .substring(0, 2)
                          .toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <span className="text-[11.5px] font-medium text-black/75 dark:text-white/75 truncate">
                  {note.author?.full_name || note.author?.email || 'Utente'}
                </span>
                <span className="text-[10px] text-black/40 dark:text-white/40">·</span>
                <span className="text-[10px] text-black/45 dark:text-white/45 whitespace-nowrap">
                  {formatDate(note.created_at)}
                </span>
                {note.is_edited && (
                  <Clock className="h-2.5 w-2.5 text-black/40 dark:text-white/40" />
                )}
                <span className="ml-auto inline-flex items-center px-1 py-px rounded text-[9px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
                  Nota
                </span>
              </div>
              <ExpandableHtmlContent
                htmlContent={note.content}
                className="text-[11.5px] text-[#37352F] dark:text-white/85 prose prose-sm dark:prose-invert max-w-none pl-6"
              />
            </div>
          );
        }

        // type === 'activity'
        const activity = item.data as any;
        return (
          <div
            key={`act-${activity.id}`}
            className="group p-2.5 bg-white/70 dark:bg-white/[0.03] hover:bg-white dark:hover:bg-white/[0.05] rounded-md border border-black/[0.06] dark:border-white/[0.06] transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-shrink-0">
                {(() => {
                  // avatar_url (admin) prevale su profile_image_url (user)
                  const photoUrl =
                    activity.assignee?.avatar_url ||
                    activity.users?.avatar_url ||
                    activity.assignee?.profile_image_url ||
                    activity.users?.profile_image_url;
                  return photoUrl ? (
                  <div className="h-4 w-4 rounded-full overflow-hidden border border-black/10">
                    <img
                      src={photoUrl}
                      alt={activity.assignee?.full_name || activity.users?.full_name || 'User'}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-4 w-4 rounded-full bg-[#F1F1EF] dark:bg-[#454B4E] flex items-center justify-center">
                    {(activity.assignee?.full_name || activity.users?.full_name) ? (
                      <span className="text-[9px] font-medium text-[#37352F] dark:text-white">
                        {(activity.assignee?.full_name || activity.users?.full_name || 'U')
                          .split(' ')
                          .map((part: string) => part[0])
                          .join('')
                          .substring(0, 2)
                          .toUpperCase()}
                      </span>
                    ) : (
                      getActivityIcon(activity)
                    )}
                  </div>
                );
                })()}
              </div>
              <span className="text-[11.5px] font-medium text-black/75 dark:text-white/75 truncate flex-1">
                {activity.title}
              </span>
              <span
                className={`inline-flex items-center px-1 py-px rounded text-[9px] font-medium flex-shrink-0 ${
                  activity.type === 'task'
                    ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                    : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                }`}
              >
                {getActivityTypeLabel(activity.type)}
              </span>
              {(activity.status === 'completed' || activity.status === 'overdue') && (
                <span
                  className={`inline-flex items-center gap-0.5 px-1 py-px rounded text-[9px] font-medium flex-shrink-0 ${getActivityStatusClass(activity.status)}`}
                >
                  {activity.status === 'completed' ? <Check className="h-2.5 w-2.5" /> : <AlertTriangle className="h-2.5 w-2.5" />}
                </span>
              )}
            </div>

            <div className="pl-6 space-y-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-black/45 dark:text-white/45">
                <span className="inline-flex items-center gap-0.5">
                  <Clock className="h-2.5 w-2.5" />
                  {formatDate(activity.created_at || new Date().toISOString())}
                </span>
                {activity.assignee && (
                  <span>· {activity.assignee.full_name || activity.assignee.email}</span>
                )}
                {activity.due_date && (
                  <span className="inline-flex items-center gap-0.5 text-orange-600 dark:text-orange-400">
                    <Calendar className="h-2.5 w-2.5" />
                    {formatDate(activity.due_date)}
                  </span>
                )}
                {activity.completed_at && (
                  <span className="inline-flex items-center gap-0.5 text-green-600 dark:text-green-400">
                    <Check className="h-2.5 w-2.5" />
                    {formatDate(activity.completed_at)}
                  </span>
                )}
              </div>

              {activity.description && (
                <ExpandableHtmlContent
                  htmlContent={activity.description}
                  className="text-[11px] text-[#37352F]/80 dark:text-white/75 prose prose-sm dark:prose-invert max-w-none"
                />
              )}

              {activity.meet_link && (
                <a
                  href={activity.meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-[10px] text-[#2383E2] dark:text-blue-400 hover:underline"
                >
                  <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Unisciti al Meet
                </a>
              )}

              {activity.status === 'completed' && activity.outcome && (
                <div className="px-1.5 py-1 bg-green-50/60 dark:bg-green-900/15 rounded border border-green-100 dark:border-green-800/40">
                  <p className="text-[9px] font-medium uppercase tracking-wide text-green-800 dark:text-green-300 mb-0.5">Esito</p>
                  <LinkClickHandler
                    htmlContent={convertUrlsToLinks(activity.outcome)}
                    className="text-[11px] text-[#37352F] dark:text-white/80 prose prose-sm dark:prose-invert max-w-none"
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
